import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users,
  Search,
  Eye,
  ShieldCheck,
  Plus,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ArrowLeft,
} from 'lucide-react'
import {
  Button,
  Badge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@jira-clone/ui'
import { UserManageModal } from './UserManageModal'
import { CreateUserModal } from './CreateUserModal'
import { api } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { useDepartments } from '../shared/hooks/useDepartments'
import { ROLES, isSuperAdminOrOwner } from '@jira-clone/shared-types'
import { cn, cardClassName } from '@jira-clone/ui'

interface User {
  id: string
  name: string
  email: string
  username?: string
  employeeId?: string
  employeeCode?: string
  department: string
  departmentRole?: string
  status: string
  role: string
  avatarUrl?: string
  permissions: Array<{ module: string; actions: string[] }>
}

type SortKey = 'name' | 'employeeId' | 'employeeCode' | 'department' | 'role' | 'status'
type SortDir = 'asc' | 'desc'

const ALL_DEPARTMENTS = 'all'

// Leadership first, then everyone else.
const ROLE_RANK: Record<string, number> = {
  [ROLES.SUPERADMIN]: 0,
  [ROLES.OWNER]: 1,
  [ROLES.ADMIN]: 2,
}

const roleRank = (role: string) => ROLE_RANK[role] ?? 3

const ROLE_BADGE: Record<string, { label: string; className: string }> = {
  [ROLES.SUPERADMIN]: {
    label: 'Super Admin',
    className: 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-950/60 dark:text-purple-300 dark:border-purple-800',
  },
  [ROLES.OWNER]: {
    label: 'Owner',
    className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800',
  },
  [ROLES.ADMIN]: {
    label: 'Admin',
    className: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800',
  },
}

function SortableHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string
  sortKey: SortKey
  sort: { key: SortKey; dir: SortDir } | null
  onSort: (key: SortKey) => void
}) {
  const active = sort?.key === sortKey
  const Icon = !active ? ChevronsUpDown : sort!.dir === 'asc' ? ChevronUp : ChevronDown
  return (
    <th className="px-4 py-3" aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground"
      >
        {label}
        <Icon className={cn('h-3 w-3', active ? 'text-foreground' : 'opacity-50')} />
      </button>
    </th>
  )
}

export default function PeoplePage() {
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const isSuperadmin = isSuperAdminOrOwner(currentUser?.role)
  const { data: departments = [] } = useDepartments()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState<string>(ALL_DEPARTMENTS)
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null)
  const [manageUserId, setManageUserId] = useState<string | null>(null)
  const [createUserOpen, setCreateUserOpen] = useState(false)

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const res = await api.get<any>('/users')
      const payload = res.data !== undefined ? res.data : res
      const rawList = Array.isArray(payload) ? payload : (payload?.data || [])
      const userList = rawList.map((u: any) => ({
        ...u,
        name: u.name || u.username || 'User',
        email: u.email || '',
        department: u.department || 'General',
        role: u.role || ROLES.MEMBER,
      }))
      setUsers(userList)
    } catch (err) {
      console.error('Failed to fetch users:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  // Every configured department (even with nobody in it yet) plus any department
  // that appears on a user but isn't in master data (e.g. "General").
  const departmentOptions = useMemo(() => {
    const counts = new Map<string, number>()
    for (const u of users) counts.set(u.department, (counts.get(u.department) ?? 0) + 1)
    const names = new Set<string>(departments.map((d) => d.name))
    counts.forEach((_, name) => names.add(name))
    return Array.from(names)
      .sort((x, y) => x.localeCompare(y))
      .map((name) => ({ name, count: counts.get(name) ?? 0 }))
  }, [users, departments])

  const filteredUsers = useMemo(() => {
    const query = search.toLowerCase().trim()
    return users.filter((u) => {
      if (departmentFilter !== ALL_DEPARTMENTS && u.department !== departmentFilter) return false
      if (!query) return true
      return (
        (u.name && u.name.toLowerCase().includes(query)) ||
        (u.email && u.email.toLowerCase().includes(query)) ||
        (u.username && u.username.toLowerCase().includes(query)) ||
        (u.employeeId && u.employeeId.toLowerCase().includes(query)) ||
        (u.department && u.department.toLowerCase().includes(query)) ||
        (u.departmentRole && u.departmentRole.toLowerCase().includes(query))
      )
    })
  }, [users, search, departmentFilter])

  const sortedUsers = useMemo(() => {
    const list = [...filteredUsers]
    const byName = (x: User, y: User) => x.name.localeCompare(y.name)
    if (!sort) {
      // Default: leadership first, then alphabetical.
      return list.sort((x, y) => roleRank(x.role) - roleRank(y.role) || byName(x, y))
    }
    const dir = sort.dir === 'asc' ? 1 : -1
    return list.sort((x, y) => {
      let cmp = 0
      if (sort.key === 'role') cmp = roleRank(x.role) - roleRank(y.role)
      else cmp = (x[sort.key] ?? '').toString().localeCompare((y[sort.key] ?? '').toString(), undefined, { numeric: true })
      return cmp * dir || byName(x, y)
    })
  }, [filteredUsers, sort])

  const toggleSort = (key: SortKey) =>
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' }
      if (prev.dir === 'asc') return { key, dir: 'desc' }
      return null
    })

  const manageUser = users.find((u) => u.id === manageUserId)

  const getInitials = (name: string) =>
    name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()

  return (
    <div className="space-y-6 w-full max-w-[1800px] mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-5">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-lg bg-primary/10 text-primary">
              <Users className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">Company People & Directory</h1>
              <p className="text-sm text-muted-foreground">
                Official employee roster. Filter by department, sort by any column
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className={cn(cardClassName(), 'px-3 py-1.5 text-center')}>
            <p className="text-[10px] text-muted-foreground uppercase font-bold">Total Staff</p>
            <p className="text-lg font-bold text-foreground">{users.length}</p>
          </div>
          <div className="px-3 py-1.5 rounded-lg border border-purple-200 bg-purple-50/50 dark:bg-purple-950/30 text-center">
            <p className="text-[10px] text-purple-600 dark:text-purple-400 uppercase font-bold">Superadmin</p>
            <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{users.filter((u) => u.role === ROLES.SUPERADMIN).length}</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className={cn(cardClassName(), 'flex items-center gap-4 p-3')}>
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search name, email, ID, role..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-xs"
          />
        </div>
        <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
          <SelectTrigger className="h-9 w-[220px] text-xs" aria-label="Filter by department">
            <SelectValue placeholder="All departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_DEPARTMENTS}>All departments ({users.length})</SelectItem>
            {departmentOptions.map((d) => (
              <SelectItem key={d.name} value={d.name}>
                {d.name} ({d.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={fetchUsers} variant="outline" size="sm">Refresh</Button>
        {isSuperadmin && (
          <Button onClick={() => setCreateUserOpen(true)} size="sm" className="gap-1.5">
            <Plus className="h-3.5 w-3.5" /> New Employee
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : sortedUsers.length === 0 ? (
        <div className={cn(cardClassName(), 'p-12 text-center')}>
          <Users className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <h3 className="text-sm font-semibold text-foreground">No users found</h3>
          <p className="text-xs text-muted-foreground mt-1">Try adjusting your search or department filter</p>
        </div>
      ) : (
        <div className={cn(cardClassName(), 'overflow-hidden')}>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border/50">
                <tr>
                  <SortableHeader label="Employee" sortKey="name" sort={sort} onSort={toggleSort} />
                  <SortableHeader label="Employee ID" sortKey="employeeId" sort={sort} onSort={toggleSort} />
                  <SortableHeader label="Employee Code" sortKey="employeeCode" sort={sort} onSort={toggleSort} />
                  <SortableHeader label="Department" sortKey="department" sort={sort} onSort={toggleSort} />
                  <th className="px-4 py-3">Designation</th>
                  <SortableHeader label="Role" sortKey="role" sort={sort} onSort={toggleSort} />
                  <th className="px-4 py-3">Email</th>
                  <SortableHeader label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {sortedUsers.map((emp) => {
                  const deptColor = departments.find((d) => d.name === emp.department)?.color || '#64748b'
                  const roleBadge = ROLE_BADGE[emp.role]
                  const isActive = (emp.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
                  return (
                    <tr
                      key={emp.id}
                      onClick={() => navigate('/app/profile/' + emp.id)}
                      className="cursor-pointer transition-colors hover:bg-muted/40"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="h-9 w-9 rounded-full flex items-center justify-center font-bold text-white text-xs shrink-0 shadow-sm"
                            style={{ backgroundColor: emp.avatarUrl || '#6366f1' }}
                          >
                            {getInitials(emp.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-foreground truncate">{emp.name}</p>
                            {emp.username && (
                              <p className="font-mono text-[11px] text-muted-foreground truncate">@{emp.username}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{emp.employeeId || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-primary">{emp.employeeCode || '—'}</td>
                      <td className="px-4 py-3">
                        <Badge
                          variant="outline"
                          className="text-xs font-semibold border"
                          style={{ color: deptColor, borderColor: `${deptColor}4d`, backgroundColor: `${deptColor}1a` }}
                        >
                          {emp.department}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{emp.departmentRole || '—'}</td>
                      <td className="px-4 py-3">
                        {roleBadge ? (
                          <Badge className={cn('text-[10px] font-bold border', roleBadge.className)}>{roleBadge.label}</Badge>
                        ) : (
                          <span className="text-[11px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            Employee
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{emp.email || '—'}</td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 text-xs font-medium',
                            isActive ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                          )}
                        >
                          <span className={cn('h-1.5 w-1.5 rounded-full', isActive ? 'bg-emerald-500' : 'bg-muted-foreground')} />
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon-sm"
                                variant="ghost"
                                aria-label={`View profile of ${emp.name}`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate('/app/profile/' + emp.id)
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>View profile</TooltipContent>
                          </Tooltip>
                          {isSuperadmin && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon-sm"
                                  variant="ghost"
                                  aria-label={`Manage access for ${emp.name}`}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setManageUserId(emp.id)
                                  }}
                                >
                                  <ShieldCheck className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Manage access</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-border/50 bg-muted/20 px-4 py-2 text-xs text-muted-foreground">
            Showing {sortedUsers.length} of {users.length} {users.length === 1 ? 'person' : 'people'}
          </div>
        </div>
      )}

      {manageUser && (
        <UserManageModal
          userId={manageUser.id}
          userName={manageUser.name}
          open={!!manageUserId}
          onOpenChange={(open) => {
            if (!open) {
              setManageUserId(null)
            }
          }}
          onSaved={fetchUsers}
        />
      )}

      <CreateUserModal open={createUserOpen} onOpenChange={setCreateUserOpen} onCreated={fetchUsers} />
    </div>
  )
}
